import { deleteField, doc, getDoc, onSnapshot, serverTimestamp, setDoc, updateDoc, type Unsubscribe } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/client";
import { appEnvironment, firebaseEnv } from "../config/env";
import type { TripSummary } from "../trips/tripStore";

export type FainanceLinkStatus = "not_linked" | "pending" | "connected" | "rejected" | "disconnected";
export type FainanceIdentifierType = "email" | "username";
export type FainanceLinkTransport = "bridge" | "profile_queue" | "local_queue";

const FAINANCE_TARGET_ENV = "test" as const;
const FAINANCE_TEST_PROJECT_ID = String(import.meta.env.VITE_FAINANCE_TEST_PROJECT_ID || "fainance-test-20260823195207");
const FAINANCE_BRIDGE_VERSION = 13;

// Fail closed: the fAInance bridge exists ONLY in Mundely Test and ONLY
// with the verified fAInance Test target. Production must never queue,
// subscribe, or call fAInance functions, even if legacy data is present.
export const fainanceIntegrationEnabled =
  appEnvironment === "test" &&
  firebaseEnv.projectId === "tripai-test-ccb16" &&
  FAINANCE_TEST_PROJECT_ID === "fainance-test-20260823195207";

export interface FainanceLinkState {
  status: FainanceLinkStatus;
  requestedIdentifier?: string;
  requestedIdentifierType?: FainanceIdentifierType;
  requestedEmail?: string;
  requestedUsername?: string;
  connectedIdentifier?: string;
  connectedEmail?: string;
  connectedUsername?: string;
  fainanceUserId?: string;
  requestedAt?: string;
  connectedAt?: string;
  rejectedAt?: string;
  disconnectedAt?: string;
  lastSyncAt?: string;
  transport?: FainanceLinkTransport;
  targetEnvironment?: "test";
  targetProjectId?: string;
}


export interface FainanceExpenseSyncPayload {
  tripId: string;
  expenseId: string;
  amount: number;
  description?: string;
  category?: string;
  currency?: string;
  date?: string;
  time?: string;
  payer?: string;
  payerId?: string;
  splitMode?: "equal" | "percentage" | "shares";
  split?: Array<{ participantId: string; participantName: string; value: number; amount: number }>;
  deleted?: boolean;
}

export interface FainanceTripSyncPayload {
  tripId: string;
  name: string;
  destination: string;
  startDate: string;
  endDate: string;
  status: TripSummary["status"];
  archived: boolean;
}

function linkDoc(uid: string) {
  return doc(db, "users", uid, "integrations", "fainance");
}

function userDoc(uid: string) {
  return doc(db, "users", uid);
}


function localKey(uid: string): string {
  return `mundely.fainance.link.${uid}`;
}

function pendingTripSyncKey(uid: string): string {
  return `mundely.fainance.tripSync.pending.v1.${uid}`;
}

function pendingExpenseSyncKey(uid: string): string {
  return `mundely.fainance.expenseSync.pending.v1.${uid}`;
}

function expensePendingId(payload: FainanceExpenseSyncPayload): string {
  return `${payload.tripId}::${payload.expenseId}`;
}

function readPendingTripSync(uid: string): Record<string, FainanceTripSyncPayload> {
  try {
    const raw = window.localStorage.getItem(pendingTripSyncKey(uid));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, FainanceTripSyncPayload>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writePendingTripSync(uid: string, pending: Record<string, FainanceTripSyncPayload>): void {
  try {
    const keys = Object.keys(pending);
    if (keys.length === 0) window.localStorage.removeItem(pendingTripSyncKey(uid));
    else window.localStorage.setItem(pendingTripSyncKey(uid), JSON.stringify(pending));
    window.dispatchEvent(new CustomEvent("mundely:fainance-trip-sync-queue", { detail: { uid, pending: keys.length } }));
  } catch {
    // Best effort. Cloud bridge remains authoritative.
  }
}

function rememberPendingTripSync(uid: string, payload: FainanceTripSyncPayload): void {
  const pending = readPendingTripSync(uid);
  pending[payload.tripId] = payload;
  writePendingTripSync(uid, pending);
}

function clearPendingTripSync(uid: string, payload: FainanceTripSyncPayload): void {
  const pending = readPendingTripSync(uid);
  if (JSON.stringify(pending[payload.tripId]) !== JSON.stringify(payload)) return;
  delete pending[payload.tripId];
  writePendingTripSync(uid, pending);
}

function readPendingExpenseSync(uid: string): Record<string, FainanceExpenseSyncPayload> {
  try {
    const raw = window.localStorage.getItem(pendingExpenseSyncKey(uid));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, FainanceExpenseSyncPayload>;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writePendingExpenseSync(uid: string, pending: Record<string, FainanceExpenseSyncPayload>): void {
  try {
    const keys = Object.keys(pending);
    if (keys.length === 0) window.localStorage.removeItem(pendingExpenseSyncKey(uid));
    else window.localStorage.setItem(pendingExpenseSyncKey(uid), JSON.stringify(pending));
    window.dispatchEvent(new CustomEvent("mundely:fainance-expense-sync-queue", { detail: { uid, pending: keys.length } }));
  } catch {
    // Best effort; Firestore workspace sync remains the second transport.
  }
}

function rememberPendingExpenseSync(uid: string, payload: FainanceExpenseSyncPayload): void {
  const pending = readPendingExpenseSync(uid);
  pending[expensePendingId(payload)] = payload;
  writePendingExpenseSync(uid, pending);
}

function clearPendingExpenseSync(uid: string, payload: FainanceExpenseSyncPayload): void {
  const pending = readPendingExpenseSync(uid);
  const key = expensePendingId(payload);
  if (JSON.stringify(pending[key]) !== JSON.stringify(payload)) return;
  delete pending[key];
  writePendingExpenseSync(uid, pending);
}

function normalizeIdentifier(value: string): { value: string; type: FainanceIdentifierType } {
  const clean = String(value || "").trim();
  if (!clean) throw new Error("invalid_identifier");
  if (clean.includes("@")) {
    const email = clean.toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error("invalid_identifier");
    return { value: email, type: "email" };
  }
  if (!/^[A-Za-z0-9._-]{3,40}$/.test(clean)) throw new Error("invalid_identifier");
  return { value: clean, type: "username" };
}

function timestampToIso(value: unknown): string | undefined {
  if (typeof value === "string" && value) return value;
  if (!value || typeof value !== "object") return undefined;
  const candidate = value as { toDate?: () => Date };
  try {
    return typeof candidate.toDate === "function" ? candidate.toDate().toISOString() : undefined;
  } catch {
    return undefined;
  }
}

function normalizeState(raw: Record<string, unknown> | undefined): FainanceLinkState {
  if (!raw) return { status: "not_linked" };
  const status = raw.status === "pending" || raw.status === "connected" || raw.status === "rejected" || raw.status === "disconnected"
    ? raw.status
    : "not_linked";
  const requestedEmail = String(raw.requestedEmail || "") || undefined;
  const requestedUsername = String(raw.requestedUsername || "") || undefined;
  const requestedIdentifier = String(raw.requestedIdentifier || "") || requestedEmail || requestedUsername;
  const requestedIdentifierType: FainanceIdentifierType | undefined = raw.requestedIdentifierType === "email" || raw.requestedIdentifierType === "username"
    ? raw.requestedIdentifierType
    : requestedEmail
      ? "email"
      : requestedUsername
        ? "username"
        : undefined;
  const connectedEmail = String(raw.connectedEmail || "") || undefined;
  const connectedUsername = String(raw.connectedUsername || "") || undefined;
  const connectedIdentifier = String(raw.connectedIdentifier || "") || connectedUsername || connectedEmail;
  const transport: FainanceLinkTransport | undefined = raw.transport === "bridge" || raw.transport === "profile_queue" || raw.transport === "local_queue"
    ? raw.transport
    : undefined;
  return {
    status,
    requestedIdentifier,
    requestedIdentifierType,
    requestedEmail,
    requestedUsername,
    connectedIdentifier,
    connectedEmail,
    connectedUsername,
    fainanceUserId: String(raw.fainanceUserId || "") || undefined,
    requestedAt: timestampToIso(raw.requestedAt),
    connectedAt: timestampToIso(raw.connectedAt),
    rejectedAt: timestampToIso(raw.rejectedAt),
    disconnectedAt: timestampToIso(raw.disconnectedAt),
    lastSyncAt: timestampToIso(raw.lastSyncAt),
    transport,
    targetEnvironment: raw.targetEnvironment === "test" ? "test" : undefined,
    targetProjectId: String(raw.targetProjectId || "") || undefined
  };
}

function readLocal(uid: string): FainanceLinkState | null {
  try {
    const raw = window.localStorage.getItem(localKey(uid));
    return raw ? normalizeState(JSON.parse(raw) as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function writeLocal(uid: string, state: FainanceLinkState): void {
  try {
    window.localStorage.setItem(localKey(uid), JSON.stringify(state));
    window.dispatchEvent(new CustomEvent("mundely:fainance-link-updated", { detail: { uid, state } }));
  } catch {
    // Local persistence is only a resilience layer.
  }
}

function clearLocal(uid: string): void {
  try {
    window.localStorage.removeItem(localKey(uid));
    window.dispatchEvent(new CustomEvent("mundely:fainance-link-updated", { detail: { uid } }));
  } catch {
    // Ignore browser storage failures.
  }
}

function stateRank(state: FainanceLinkState | null | undefined): number {
  if (!state) return 0;
  if (state.status === "connected") return 5;
  if (state.status === "pending") return 4;
  if (state.status === "rejected") return 3;
  if (state.status === "disconnected") return 2;
  return 1;
}

function bestState(...states: Array<FainanceLinkState | null | undefined>): FainanceLinkState {
  const trusted = states.filter((state): state is FainanceLinkState => Boolean(state)).filter((state) => {
    if (state.status !== "pending") return true;
    if (state.transport !== "bridge") return false;
    if (state.targetEnvironment && state.targetEnvironment !== FAINANCE_TARGET_ENV) return false;
    if (state.targetProjectId && state.targetProjectId !== FAINANCE_TEST_PROJECT_ID) return false;
    return true;
  });
  return trusted.sort((a, b) => stateRank(b) - stateRank(a))[0] || { status: "not_linked" };
}

async function getProfileQueuedState(uid: string): Promise<FainanceLinkState | null> {
  try {
    const snapshot = await getDoc(userDoc(uid));
    if (!snapshot.exists()) return null;
    const raw = snapshot.data() as { integrations?: { fainance?: Record<string, unknown> } };
    return raw.integrations?.fainance ? normalizeState(raw.integrations.fainance) : null;
  } catch {
    return null;
  }
}

async function getLegacySubdocumentState(uid: string): Promise<FainanceLinkState | null> {
  try {
    const snapshot = await getDoc(linkDoc(uid));
    return snapshot.exists() ? normalizeState(snapshot.data()) : null;
  } catch {
    return null;
  }
}

export async function getFainanceLinkState(uid: string): Promise<FainanceLinkState> {
  if (!fainanceIntegrationEnabled) return { status: "not_linked" };
  const [legacy, profileQueue] = await Promise.all([getLegacySubdocumentState(uid), getProfileQueuedState(uid)]);
  return bestState(legacy, profileQueue, readLocal(uid));
}

export function subscribeFainanceLinkState(uid: string, callback: (state: FainanceLinkState) => void): Unsubscribe {
  if (!fainanceIntegrationEnabled) { callback({ status: "not_linked" }); return () => undefined; }
  let legacy: FainanceLinkState | null = null;
  let profileQueue: FainanceLinkState | null = null;
  let local = readLocal(uid);
  let stopped = false;

  const emit = () => { if (!stopped) callback(bestState(legacy, profileQueue, local)); };

  const unsubLegacy = onSnapshot(linkDoc(uid), (snapshot) => {
    legacy = snapshot.exists() ? normalizeState(snapshot.data()) : null;
    emit();
  }, () => emit());

  const unsubProfile = onSnapshot(userDoc(uid), (snapshot) => {
    const raw = snapshot.exists() ? snapshot.data() as { integrations?: { fainance?: Record<string, unknown> } } : undefined;
    profileQueue = raw?.integrations?.fainance ? normalizeState(raw.integrations.fainance) : null;
    emit();
  }, () => emit());

  const onLocal = (event: Event) => {
    const detail = (event as CustomEvent<{ uid?: string }>).detail;
    if (detail?.uid !== uid) return;
    local = readLocal(uid);
    emit();
  };
  window.addEventListener("mundely:fainance-link-updated", onLocal as EventListener);
  emit();

  return () => {
    stopped = true;
    unsubLegacy();
    unsubProfile();
    window.removeEventListener("mundely:fainance-link-updated", onLocal as EventListener);
  };
}

async function tryBridgeAction<TInput extends Record<string, unknown>>(name: string, input: TInput): Promise<boolean> {
  if (!fainanceIntegrationEnabled) return false;
  try {
    const callable = httpsCallable<TInput, { ok?: boolean; environment?: string; targetProjectId?: string; bridgeVersion?: number }>(functions, name);
    const result = await callable({ ...input, bridgeVersion: FAINANCE_BRIDGE_VERSION });
    const data = result.data || {};
    return data.ok === true
      && data.environment === FAINANCE_TARGET_ENV
      && data.targetProjectId === FAINANCE_TEST_PROJECT_ID
      && Number(data.bridgeVersion || 0) >= FAINANCE_BRIDGE_VERSION;
  } catch {
    return false;
  }
}


export async function requestFainanceLink(uid: string, identifier: string): Promise<FainanceLinkState> {
  if (!fainanceIntegrationEnabled) throw new Error("fainance_integration_disabled");
  const normalized = normalizeIdentifier(identifier);
  const base: FainanceLinkState = {
    status: "pending",
    requestedIdentifier: normalized.value,
    requestedIdentifierType: normalized.type,
    requestedEmail: normalized.type === "email" ? normalized.value : undefined,
    requestedUsername: normalized.type === "username" ? normalized.value : undefined,
    requestedAt: new Date().toISOString(),
    transport: "bridge",
    targetEnvironment: FAINANCE_TARGET_ENV,
    targetProjectId: FAINANCE_TEST_PROJECT_ID
  };

  const bridged = await tryBridgeAction("requestFainanceLink", {
    identifier: normalized.value,
    identifierType: normalized.type,
    sourceApp: "mundely",
    targetEnvironment: FAINANCE_TARGET_ENV,
    targetProjectId: FAINANCE_TEST_PROJECT_ID
  });
  if (!bridged) throw new Error("fainance_test_bridge_unavailable");

  writeLocal(uid, base);
  return base;
}

export async function cancelFainanceLinkRequest(uid: string): Promise<void> {
  if (!fainanceIntegrationEnabled) throw new Error("fainance_integration_disabled");
  clearLocal(uid);
  const bridged = await tryBridgeAction("cancelFainanceLink", {
    sourceApp: "mundely",
    targetEnvironment: FAINANCE_TARGET_ENV,
    targetProjectId: FAINANCE_TEST_PROJECT_ID
  });
  if (!bridged) throw new Error("fainance_test_bridge_unavailable");
  try {
    await updateDoc(userDoc(uid), { "integrations.fainance": deleteField() });
  } catch {
    // The Test bridge is authoritative; profile cleanup is best-effort.
  }
  try {
    await setDoc(linkDoc(uid), {
      status: "not_linked",
      requestedIdentifier: deleteField(),
      requestedIdentifierType: deleteField(),
      requestedEmail: deleteField(),
      requestedUsername: deleteField(),
      requestedAt: deleteField(),
      rejectedAt: deleteField(),
      disconnectedAt: deleteField()
    }, { merge: true });
  } catch {
    // Legacy path can be unavailable under stricter Firestore rules.
  }
}

export async function disconnectFainance(uid: string): Promise<void> {
  if (!fainanceIntegrationEnabled) throw new Error("fainance_integration_disabled");
  const state: FainanceLinkState = { status: "disconnected", disconnectedAt: new Date().toISOString() };
  writeLocal(uid, state);
  const bridged = await tryBridgeAction("disconnectFainanceLink", {
    sourceApp: "mundely",
    targetEnvironment: FAINANCE_TARGET_ENV,
    targetProjectId: FAINANCE_TEST_PROJECT_ID
  });
  if (!bridged) throw new Error("fainance_test_bridge_unavailable");
}

const syncTasks = new Map<string, Promise<unknown>>();

function serializeSync<T>(uid: string, action: () => Promise<T>): Promise<T> {
  const previous = syncTasks.get(uid) || Promise.resolve();
  const task = previous.catch(() => undefined).then(action);
  syncTasks.set(uid, task);
  void task.finally(() => { if (syncTasks.get(uid) === task) syncTasks.delete(uid); }).catch(() => undefined);
  return task;
}

export async function queueTripForFainanceShareSync(uid: string, trip: TripSummary): Promise<boolean> {
  if (!fainanceIntegrationEnabled) return false;

  const payload: FainanceTripSyncPayload = {
    tripId: trip.id,
    name: trip.name,
    destination: trip.destination,
    startDate: trip.startDate,
    endDate: trip.endDate,
    status: trip.status,
    archived: Boolean(trip.archivedAt)
  };

  rememberPendingTripSync(uid, payload);
  if (typeof navigator !== "undefined" && !navigator.onLine) return false;

  return serializeSync(uid, async () => {
  const latest = readPendingTripSync(uid)[payload.tripId];
  if (!latest) return true;
  const link = await getFainanceLinkState(uid);
  if (link.status !== "connected") return false;
  const bridged = await tryBridgeAction("queueFainanceShareSync", {
    ...latest,
    sourceApp: "mundely",
    targetEnvironment: FAINANCE_TARGET_ENV,
    targetProjectId: FAINANCE_TEST_PROJECT_ID
  });
  if (bridged) clearPendingTripSync(uid, latest);
  return bridged;
  });
}

export async function flushPendingFainanceTripSync(uid: string): Promise<number> {
  if (!fainanceIntegrationEnabled) return 0;
  return serializeSync(uid, async () => {
  if (typeof navigator !== "undefined" && !navigator.onLine) return 0;
  const link = await getFainanceLinkState(uid);
  if (link.status !== "connected") return 0;
  const pending = readPendingTripSync(uid);
  let completed = 0;
  for (const payload of Object.values(pending)) {
    const bridged = await tryBridgeAction("queueFainanceShareSync", {
      ...payload,
      sourceApp: "mundely",
      targetEnvironment: FAINANCE_TARGET_ENV,
      targetProjectId: FAINANCE_TEST_PROJECT_ID
    });
    if (!bridged) continue;
    clearPendingTripSync(uid, payload);
    completed += 1;
  }
  return completed;
  });
}

export async function flushPendingFainanceExpenseSync(uid: string): Promise<number> {
  if (!fainanceIntegrationEnabled) return 0;
  return serializeSync(uid, async () => {
  if (typeof navigator !== "undefined" && !navigator.onLine) return 0;
  const link = await getFainanceLinkState(uid);
  if (link.status !== "connected") return 0;
  const pending = readPendingExpenseSync(uid);
  let completed = 0;
  for (const payload of Object.values(pending)) {
    const bridged = await tryBridgeAction("queueFainanceExpenseSync", {
      ...payload,
      sourceApp: "mundely",
      targetEnvironment: FAINANCE_TARGET_ENV,
      targetProjectId: FAINANCE_TEST_PROJECT_ID
    });
    if (!bridged) continue;
    clearPendingExpenseSync(uid, payload);
    completed += 1;
  }
  return completed;
  });
}

export function startFainanceTripSyncRetry(uid: string): () => void {
  if (!fainanceIntegrationEnabled) return () => undefined;
  let stopped = false;
  let running = false;
  let debounceTimer: number | undefined;
  const flush = async () => {
    if (stopped || running) return;
    if (typeof navigator !== "undefined" && !navigator.onLine) return;
    running = true;
    try {
      await Promise.all([flushPendingFainanceTripSync(uid), flushPendingFainanceExpenseSync(uid)]);
    } finally {
      running = false;
    }
  };
  const scheduleFlush = () => {
    if (stopped) return;
    if (debounceTimer) window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => { debounceTimer = undefined; void flush(); }, 180);
  };
  const onVisibility = () => { if (document.visibilityState === "visible") scheduleFlush(); };
  window.addEventListener("online", scheduleFlush);
  window.addEventListener("focus", scheduleFlush);
  window.addEventListener("tripai:trips-updated", scheduleFlush);
  window.addEventListener("tripai:workspace-updated", scheduleFlush);
  window.addEventListener("mundely:fainance-link-updated", scheduleFlush);
  window.addEventListener("mundely:fainance-trip-sync-queue", scheduleFlush);
  window.addEventListener("mundely:fainance-expense-sync-queue", scheduleFlush);
  document.addEventListener("visibilitychange", onVisibility);
  const timer = window.setInterval(() => void flush(), 8000);
  scheduleFlush();
  return () => {
    stopped = true;
    if (debounceTimer) window.clearTimeout(debounceTimer);
    window.removeEventListener("online", scheduleFlush);
    window.removeEventListener("focus", scheduleFlush);
    window.removeEventListener("tripai:trips-updated", scheduleFlush);
    window.removeEventListener("tripai:workspace-updated", scheduleFlush);
    window.removeEventListener("mundely:fainance-link-updated", scheduleFlush);
    window.removeEventListener("mundely:fainance-trip-sync-queue", scheduleFlush);
    window.removeEventListener("mundely:fainance-expense-sync-queue", scheduleFlush);
    document.removeEventListener("visibilitychange", onVisibility);
    window.clearInterval(timer);
  };
}

export async function queueExpenseForFainanceShareSync(uid: string, expense: FainanceExpenseSyncPayload): Promise<boolean> {
  if (!fainanceIntegrationEnabled) return false;
  if (!expense.tripId || !expense.expenseId) return false;
  rememberPendingExpenseSync(uid, expense);
  if (typeof navigator !== "undefined" && !navigator.onLine) return false;
  return serializeSync(uid, async () => {
  const latest = readPendingExpenseSync(uid)[expensePendingId(expense)];
  if (!latest) return true;
  const link = await getFainanceLinkState(uid);
  if (link.status !== "connected") return false;
  const bridged = await tryBridgeAction("queueFainanceExpenseSync", {
    ...latest,
    sourceApp: "mundely",
    targetEnvironment: FAINANCE_TARGET_ENV,
    targetProjectId: FAINANCE_TEST_PROJECT_ID
  });
  if (bridged) clearPendingExpenseSync(uid, latest);
  return bridged;
  });
}

export async function markFainanceLinkSeen(uid: string): Promise<void> {
  if (!fainanceIntegrationEnabled) return;
  try {
    await setDoc(linkDoc(uid), { lastSeenAt: serverTimestamp() }, { merge: true });
  } catch {
    // Optional acknowledgement only.
  }
}
